import { boundaryPolygonPaths } from "../../utils/fieldBoundaryGeometry";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
  Polygon,
} from "@react-google-maps/api";
import { IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CropYearStatusBadge from "../CropYearStatusBadge";
import { formatAcres } from "../../utils/acreageFormatting.js";

const DEFAULT_CENTER = {
  lat: 57.534346,
  lng: -101.925502,
};

const SELECTED_POLYGON_OPTIONS = {
  fillColor: "#2563eb",
  fillOpacity: 0.2,
  strokeColor: "#2563eb",
  strokeOpacity: 1,
  strokeWeight: 3,
};

const REFERENCE_POLYGON_OPTIONS = {
  fillColor: "#BF00FF",
  fillOpacity: 0.12,
  strokeColor: "#BF00FF",
  strokeOpacity: 0.95,
  strokeWeight: 2,
};

const GOOGLE_MAP_LIBRARIES = ["geometry"];

function transformCropName(cropName) {
  if (!cropName) {
    return "Draft";
  }

  return cropName
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");
}

function calculateCentroid(boundaries = []) {
  if (!Array.isArray(boundaries) || boundaries.length === 0) {
    return null;
  }

  const totals = boundaries.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: totals.lat / boundaries.length,
    lng: totals.lng / boundaries.length,
  };
}

function getFieldMapPoint(field) {
  return field.centroid || calculateCentroid(field.boundaries) || null;
}

function getFieldBounds(field) {
  if (!Array.isArray(field.boundaries) || field.boundaries.length === 0) {
    return null;
  }

  return field.boundaries.reduce(
    (acc, point) => ({
      north: Math.max(acc.north, point.lat),
      south: Math.min(acc.south, point.lat),
      east: Math.max(acc.east, point.lng),
      west: Math.min(acc.west, point.lng),
    }),
    {
      north: field.boundaries[0].lat,
      south: field.boundaries[0].lat,
      east: field.boundaries[0].lng,
      west: field.boundaries[0].lng,
    }
  );
}

function sortCropYears(cropYears = []) {
  return [...cropYears].sort((left, right) => {
    const leftYear = left.cropYear ?? -Infinity;
    const rightYear = right.cropYear ?? -Infinity;

    return rightYear - leftYear;
  });
}

function FieldNavigator({
  selectedField,
  selectedIndex,
  totalFields,
  onGoPrevious,
  onGoNext,
  onOpenField,
}) {
  if (!selectedField) {
    return (
      <div className="rounded-sm border border-[#d6d6d6] bg-white p-5">
        <p className="font-semibold text-[#243245]">No mappable field selected</p>
        <p className="mt-2 text-sm text-[#666666]">
          Choose a field polygon or marker on the map to explore it here.
        </p>
      </div>
    );
  }

  const cropYears = sortCropYears(selectedField.cropYears);

  return (
    <div className="rounded-sm border border-[#d6d6d6] bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-[#6b7280]">
            Map Mode
          </p>
          <h3 className="mt-1 text-2xl font-semibold text-[#243245]">
            {selectedField.fieldName}
          </h3>
          <p className="mt-1 text-sm text-[#666666]">
            {selectedField.farmName}
            {selectedField.province ? ` • ${selectedField.province}` : ""}
          </p>
        </div>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-sm border border-[#2563eb] px-3 py-2 text-sm font-medium text-[#2563eb]"
          onClick={onOpenField}
        >
          Open Field
          <OpenInNewIcon fontSize="small" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm text-[#4b5563]">
        <div className="rounded-sm bg-[#f6f6f6] px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-[#6b7280]">Acres</p>
          <p className="mt-1 font-semibold text-[#243245]">
            {formatAcres(
              selectedField.fieldSize || selectedField.fieldBoundariesSize,
              "Unknown"
            )}
          </p>
        </div>
        <div className="rounded-sm bg-[#f6f6f6] px-3 py-2">
          <p className="text-xs uppercase tracking-wide text-[#6b7280]">Crop Years</p>
          <p className="mt-1 font-semibold text-[#243245]">{cropYears.length}</p>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between rounded-sm bg-[#f3ebff] px-3 py-2">
        <div>
          <p className="text-xs uppercase tracking-wide text-[#7e22ce]">
            Field Navigation
          </p>
          <p className="text-sm text-[#6b21a8]">
            Field {selectedIndex + 1} of {totalFields}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton
            onClick={onGoPrevious}
            disabled={totalFields <= 1}
            aria-label="Previous field"
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={onGoNext}
            disabled={totalFields <= 1}
            aria-label="Next field"
          >
            <ChevronRightIcon />
          </IconButton>
        </div>
      </div>

      <div className="mt-5">
        <p className="text-sm font-semibold text-[#243245]">Crop Years</p>
        {cropYears.length === 0 ? (
          <div className="mt-3 rounded-sm border border-dashed border-[#d6d6d6] bg-[#fafafa] px-4 py-3 text-sm text-[#666666]">
            No crop years are on this field yet. You can still open the field page
            from here.
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-3">
            {cropYears.map((cropYear) => (
              <div
                key={cropYear.cropyearId}
                className="rounded-sm border border-[#e5e7eb] bg-[#fafafa] px-4 py-3"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#243245]">
                      {cropYear.cropYear || "Draft Year"} •{" "}
                      {transformCropName(cropYear.crop)}
                    </p>
                    <p className="mt-1 text-xs text-[#666666]">
                      Crop year record for this field.
                    </p>
                  </div>
                  <CropYearStatusBadge cropYear={cropYear} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardMapView({
  fields,
  isLoading,
  selectedFieldId,
  onSelectField,
  onOpenField,
  onGoPreviousField,
  onGoNextField,
  GOOGLE_APIKEY,
}) {
  const [map, setMap] = useState(null);
  const [hoveredFieldId, setHoveredFieldId] = useState(null);

  const selectedField = useMemo(
    () => fields.find((field) => field.fieldId === selectedFieldId) || null,
    [fields, selectedFieldId]
  );

  const selectedIndex = useMemo(
    () => fields.findIndex((field) => field.fieldId === selectedFieldId),
    [fields, selectedFieldId]
  );

  useEffect(() => {
    if (!map || !window.google || !selectedField) {
      return;
    }

    const fieldBounds = getFieldBounds(selectedField);

    if (fieldBounds) {
      const nextBounds = new window.google.maps.LatLngBounds();
      nextBounds.extend({ lat: fieldBounds.north, lng: fieldBounds.east });
      nextBounds.extend({ lat: fieldBounds.south, lng: fieldBounds.west });
      map.fitBounds(nextBounds, 80);
      return;
    }

    const point = getFieldMapPoint(selectedField);

    if (point) {
      map.panTo(point);
      map.setZoom(14);
    }
  }, [map, selectedField]);

  const mappableFields = fields.filter(
    (field) =>
      (Array.isArray(field.boundaries) && field.boundaries.length > 2) ||
      getFieldMapPoint(field)
  );

  if (isLoading) {
    return (
      <div className="flex h-[640px] w-full items-center justify-center rounded-sm border border-[#d6d6d6] bg-white text-[#666666]">
        Loading map mode...
      </div>
    );
  }

  if (mappableFields.length === 0) {
    return (
      <div className="flex h-[640px] w-full items-center justify-center rounded-sm border border-[#d6d6d6] bg-white text-center text-[#666666]">
        <div>
          <p className="font-semibold text-[#243245]">No mapped fields found</p>
          <p className="mt-2 max-w-[420px] text-sm">
            Try clearing filters or add field boundaries to more fields so they can
            appear in map mode.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-[640px] w-full grid-cols-1 gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
      <FieldNavigator
        selectedField={selectedField}
        selectedIndex={selectedIndex < 0 ? 0 : selectedIndex}
        totalFields={mappableFields.length}
        onGoPrevious={onGoPreviousField}
        onGoNext={onGoNextField}
        onOpenField={onOpenField}
      />

      <div className="overflow-hidden rounded-sm border border-[#d6d6d6] bg-white">
        {!GOOGLE_APIKEY ? (
          <div className="flex h-full items-center justify-center text-[#666666]">
            Map credentials are unavailable right now.
          </div>
        ) : (
          <LoadScript
            id="dashboard-map-script"
            googleMapsApiKey={GOOGLE_APIKEY}
            libraries={GOOGLE_MAP_LIBRARIES}
          >
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={DEFAULT_CENTER}
              zoom={4}
              options={{
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: false,
                rotateControl: false,
                mapTypeId: "hybrid",
                tilt: 0,
              }}
              onLoad={(loadedMap) => setMap(loadedMap)}
            >
              {mappableFields.map((field) => {
                const point = getFieldMapPoint(field);
                const isSelected = field.fieldId === selectedFieldId;
                const isHovered = field.fieldId === hoveredFieldId;

                return (
                  <Fragment key={field.fieldId}>
                    {Array.isArray(field.boundaries) && field.boundaries.length > 2 ? (
                      boundaryPolygonPaths(field.boundaries).map((paths, index) => <Polygon
                        key={index}
                        paths={paths}
                        options={
                          isSelected
                            ? SELECTED_POLYGON_OPTIONS
                            : REFERENCE_POLYGON_OPTIONS
                        }
                        onClick={() => onSelectField(field.fieldId)}
                        onMouseOver={() => setHoveredFieldId(field.fieldId)}
                        onMouseOut={() => setHoveredFieldId(null)}
                      />)
                    ) : null}
                    {point ? (
                      <Marker
                        position={point}
                        onClick={() => onSelectField(field.fieldId)}
                        icon={
                          window.google
                            ? {
                                url: `data:image/svg+xml;charset=UTF-8,%3Csvg height='22' viewBox='0 0 22 22' width='22' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='7' fill='${encodeURIComponent(
                                  isSelected ? "#2563eb" : "#BF00FF"
                                )}'/%3E%3C/svg%3E`,
                                scaledSize: new window.google.maps.Size(14, 14),
                                anchor: new window.google.maps.Point(7, 7),
                              }
                            : undefined
                        }
                      />
                    ) : null}
                    {isHovered && point ? (
                      <InfoWindow
                        position={point}
                        onCloseClick={() => setHoveredFieldId(null)}
                      >
                        <div className="min-w-[180px] text-[#243245]">
                          <p className="font-semibold">{field.fieldName}</p>
                          <p className="text-sm">{field.farmName}</p>
                          <p className="text-xs text-[#666666]">
                            {field.province || "Province unknown"}
                          </p>
                          <p className="mt-1 text-xs text-[#666666]">
                            {formatAcres(
                              field.fieldSize || field.fieldBoundariesSize,
                              "Unknown"
                            )}{" "}
                            acres
                          </p>
                        </div>
                      </InfoWindow>
                    ) : null}
                  </Fragment>
                );
              })}
            </GoogleMap>
          </LoadScript>
        )}
      </div>
    </div>
  );
}
